import { AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild, DestroyRef, inject} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms'
import { BranchWithStatus } from '@app/models/branch.model'
import { Client } from '@app/models/client.model'
import { EventWithStatus } from '@app/models/event.model'
import { ComponentState, ListConfig, RadioListConfig } from '@app/models/shared.model'
import { pick } from 'lodash-es'
import { Subscription, take } from 'rxjs'
import { InputMaskService } from '../../services/input-mask.service'
import { BranchService } from '@app/modules/authorized/services/branch.service'
import { DetailsService } from '../../pages/settings/services/details.service'
import { AuthService } from '@app/core/auth/services/auth.service'
import { CommonService } from '@app/shared/services/common.service'
import { DateService } from '@app/shared/services/date.service'
import { MyTreeComponent } from '../myTree/myTree.component'
import { RestrictionsComponent } from '../restrictions/restrictions.component'
import { DynamicFormControlValidatorService } from '@app/shared/services/dynamic-form-control-validator.service'
import { Area } from '@app/models/area.model'
import { ClientRole } from '@app/models/client_role.model'
import { ClientRoleService } from '@app/modules/nursing/settings/services/client-role.service'
import { TreeNode } from 'primeng/api'

@Component({
  selector: 'app-modal-invitation-base',
  templateUrl: './modal-invitation-base.component.html',
  styleUrls: ['./modal-invitation-base.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush, // Performance optimization for large component
  standalone: false,
})
export class ModalInvitationBaseComponent implements OnInit, AfterViewChecked, AfterViewInit {
  protected destroyRef = inject(DestroyRef)
  /** Tree reference for "update state" (create/edit) control filter/collapse... */
  @ViewChild('updateTreeRef') updateTreeRef: MyTreeComponent

  /** Tree reference for "details state" control filter/collapse... */
  @ViewChild('detailsTreeRef') detailsTreeRef: MyTreeComponent

  /** Restrictions component reference */
  @ViewChild('restrictionsRef') restrictionsRef: RestrictionsComponent

  /** Save current client */
  @Input() client: Client | any

  /** Whether to exclude special area types (vehicle and HME). true = exclude vehicle/HME, false = include all, undefined = show all */
  @Input() excludeSpecialAreaTypes: boolean | undefined = undefined

  allClientRoles: ClientRole[] = []

  /** Branches array of objects */
  branches: Partial<BranchWithStatus>[] = []

  /** Duplicated branches array of objects use when change state from details to edit */
  duplicatedBranches: Partial<BranchWithStatus>[] = []

  /** On close output emit */
  @Output() onClose = new EventEmitter<any>()

  /** On create output, invoke when create event */
  @Output() onCreate = new EventEmitter<any>()

  /** State of component details/edit/create */
  state: ComponentState

  /** Form visible flag */
  visible: boolean = false

  /** Data loaded flag */
  loaded: boolean = false

  /** Invitation model */
  model: any = {}

  /** Form group, save form data and validators */
  form: FormGroup

  /** Model dates oject for bind to "invitation_calendar" component */
  modelDates: any = {}

  /** For bind selected branch */
  selectedBranch: any = null

  /** Default form state */
  defaultFormState: any = {}

  /** Selected checkbox entrance for bind */
  selectedEntrance: BranchWithStatus

  /** Areas array */
  areas: any[]

  /** Selected areas */
  selectedAreas: any[]

  /** Relations */
  relations: any[] = []

  /** Period start key name */
  periodStartKeyName: string = 'starting_date'

  /** Period end key name */
  periodEndKeyName: string = 'ending_date'

  /** Period start value name */
  periodStartValueName: string = 'limitations_start'

  /** Period end value name */
  periodEndValueName: string = 'limitations_end'

  /** Tracks whether the user has explicitly set a starting date via the calendar */
  hasStartingDate: boolean = false


  expanded: boolean

  /** Set default branch on change */
  setDefaultBranch: boolean = true

  /** Active module name */
  activeModuleName: string

  /** Current user */
  user: any

  /** Branches list config */
  branchesListConfig: ListConfig = new ListConfig({
    data: [],
    optionValue: 'id',
    filter: false,
    template: 'valueAndAvatar',
    templateKey: 'name',
    showClear: true,
    selectedItemTemplate: false,
  })

  /** Is first change if the branch use for init the branch time limitation  */
  firstChange: boolean

  /** Client roles list config */
  clientRolesListConfig: ListConfig = { data: [], optionLabel: 'name', optionValue: 'id', scrollHeight: '200px', showClear: true }

  /** The strip of feature checkboxes (open once / transferable / issue permit / book rooms …).
   *  Shared by the pass and employee modals, which each fill it in their constructor. */
  checkboxListConfig: RadioListConfig[] = []

  assignableClientRolesListConfig: ListConfig = new ListConfig({
    data: [],
    optionValue: 'id',
    template: 'translated',
    filter: false,
    multiSelectionEmptyLabel: 'EMPLOYEES.FORMS.FIELD_LABELS.ASSIGNED_GROUPS',
    multiSelectionSeparationValue: ',',
    initUsingSelectItemInterface: true,
    templateKey: 'name',
  })

  /**
   * The rooms of the selected branch, for the pass's own "rooms this pass may book" picker. Built from
   * the branch already loaded for the areas tree, and rebuilt only when the branch changes — see
   * bookableRoomsListConfig.
   */
  private readonly bookableRoomsList: ListConfig = new ListConfig({
    data: [],
    optionValue: 'id',
    filter: true,
    template: 'doubleWithSpace',
    templateKey: 'name',
    multiSelectionEmptyLabel: 'AUTHORIZED.LABELS.BOOKING_ROOMS',
    multiSelectionSeparationValue: ',',
    filterBy: 'name',
    scrollHeight: '200px',
    selectedItemTemplate: true,
    showClear: true,
  })

  private bookableRoomsBranch: unknown = undefined

  /** The bookable rooms of `selectedBranch`, each listed once even when it sits on two gates. */
  get bookableRoomsListConfig(): ListConfig {
    if (this.selectedBranch !== this.bookableRoomsBranch) {
      this.bookableRoomsBranch = this.selectedBranch
      const rooms = new Map<number, Area>()
      for (const entrance of this.selectedBranch?.entrances ?? []) {
        for (const area of entrance?.areas ?? []) {
          if (area?.active && String(area.type ?? '').toLowerCase() === 'bookable') rooms.set(Number(area.id), { ...area, id: Number(area.id) })
        }
      }
      this.bookableRoomsList.data = [...rooms.values()]
    }
    return this.bookableRoomsList
  }

  /**
   * What to SEND for `booking_area_ids` — the rooms this pass may book, ahead of its role's.
   *
   * Only while the pass may book at all: unticking the box drops the list with it, so ticking it
   * again later does not bring back rooms nobody can see. An empty pick is `null` — no list of its
   * own, the role's rooms apply — and `null` rather than an omitted key, so clearing it saves.
   */
  protected resolveBookingAreaIds(bookingChecked: boolean | null | undefined, roomIds: unknown): number[] | null {
    if (!bookingChecked || !Array.isArray(roomIds) || !roomIds.length) return null
    return roomIds.map(Number)
  }

  /**
   * The rooms the selected role lets this pass book, which is what an EMPTY picker means.
   *
   * QA 19/10/2026: a role allowed one room, and the card's picker showed nothing, which read as
   * "this person may book nothing". The picker stays empty on purpose — copying the role's rooms
   * into it would give the pass a list of its own that no longer follows the role — so the hint
   * names them instead. `null` = the role lists no rooms, so every room applies; '' = it lists
   * rooms, none of them in this branch.
   */
  roleBookingRoomNames(): string | null {
    const roleId = this.form?.get('client_role')?.value
    const role = ((this.clientRolesListConfig?.data as ClientRole[] | undefined) || []).find((r) => r.id?.toString() === String(roleId))
    const ids = role?.booking?.area_ids
    if (!Array.isArray(ids) || !ids.length) return null
    const names = new Map((this.bookableRoomsListConfig.data as Area[]).map((room) => [Number(room.id), room.name]))
    return ids
      .map((id) => names.get(Number(id)))
      .filter(Boolean)
      .join(', ')
  }

  /** The picked rooms by name, for the read-only drawer; '' when the pass has none of its own. */
  bookingRoomNames(roomIds: unknown): string {
    if (!Array.isArray(roomIds) || !roomIds.length) return ''
    const names = new Map((this.bookableRoomsListConfig.data as Area[]).map((room) => [Number(room.id), room.name]))
    return roomIds.map((id) => names.get(Number(id)) ?? `#${id}`).join(', ')
  }

  constructor(
    public fb: FormBuilder,
    public commonService: CommonService,
    public dateService: DateService,
    public branchService: BranchService,
    public detailsService: DetailsService,
    public cdRef: ChangeDetectorRef,
    public maskService: InputMaskService,
    public authService: AuthService,
    public formService: DynamicFormControlValidatorService,
    private clientRoleService?: ClientRoleService
  ) {}

  ngOnInit(): void {
    this.authService.getUser().then((user: any) => {
      this.user = user
    })
  }


  ngAfterViewChecked() {
    this.expanded = this.state != 'details' ? this.updateTreeRef?.isExpanded : this.detailsTreeRef?.isExpanded
    if (this.branches.length == 1 && this.selectedBranch && this.restrictionsRef)
      if (!this.restrictionsRef.selectedRestrictions?.length && this.firstChange) {
        this.firstChange = false
        this.setDefaultValueToLimitations(this.state == 'create' ? this.selectedBranch : this.model)
      }

    this.cdRef.markForCheck()
  }

  ngAfterViewInit(): void {
    if (this.restrictionsRef && !this.restrictionsRef?.selectedRestrictions?.length) this.setDefaultValueToLimitations(this.selectedBranch)
    this.cdRef.markForCheck()
  }

  get entrances() {
    return this.selectedBranch?.entrances
      ?.filter((e: any) => e.name != 'event_unused_entrance')
      .map((entrance: any) => {
        let filteredAreas = entrance.areas.filter((area: any) => area.active)

        // Apply special area type filtering if specified
        if (this.excludeSpecialAreaTypes !== undefined) {
          filteredAreas = filteredAreas.filter((area: any) => {
            const areaType = area.type?.toLowerCase()
            const isSpecialArea = areaType === 'vehicle' || areaType === 'hme'
            // If excludeSpecialAreaTypes is true, filter out special areas; otherwise include only special areas
            return this.excludeSpecialAreaTypes ? !isSpecialArea : isSpecialArea
          })
        }

        return {
          ...entrance,
          areas: filteredAreas,
        }
      })
      .filter((entrance: any) => entrance.areas.length > 0) // Remove entrances with no areas after filtering
  }

  get branchRelations() {
    return this.relations?.filter((re: any) => re.event_id.toString() == this.selectedBranch?.id.toString())
  }

  /** Position of the sidebar */
  get position() {
    return document.dir == 'rtl' ? 'left' : 'right'
  }

  get filteredAreas() {
    let filteredAreas = this.areas?.filter((v) => v.active)

    // Apply special area type filtering if specified
    if (this.excludeSpecialAreaTypes !== undefined && filteredAreas) {
      filteredAreas = filteredAreas.filter((area) => {
        const isSpecialArea = this.isVehicleArea(area) || this.isHMEArea(area)
        // If excludeSpecialAreaTypes is true, filter out special areas; otherwise include only special areas
        return this.excludeSpecialAreaTypes ? !isSpecialArea : isSpecialArea
      })
    }

    return filteredAreas
  }

  /**
   * Check if an area is a vehicle area by looking at its type in the selected branch
   */
  private isVehicleArea(area: any): boolean {
    if (!this.selectedBranch?.entrances) return false

    const matchingArea = this.selectedBranch.entrances
      .flatMap((entrance: any) => entrance.areas || [])
      .find((entranceArea: any) => entranceArea.id.toString() === area.id.toString())

    return matchingArea?.type?.toLowerCase() === 'vehicle'
  }

  /**
   * Check if an area is an HME area by looking at its type in the selected branch
   */
  private isHMEArea(area: any): boolean {
    if (!this.selectedBranch?.entrances) return false

    const matchingArea = this.selectedBranch.entrances
      .flatMap((entrance: any) => entrance.areas || [])
      .find((entranceArea: any) => entranceArea.id.toString() === area.id.toString())

    return matchingArea?.type?.toLowerCase() === 'hme'
  }

  fetchData() {
    this.form
      ?.get('create_pass')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value) {
          this.form.get('transferable')!.setValue(false)
          this.form.get('transferable')!.disable()
        } else this.form.get('transferable')!.enable()
      })

    if (this.state != 'create') {
      this.hasStartingDate = true
      this.modelDates[this.periodStartKeyName] = this.dateService.convertToLocalDate(this.model[this.periodStartValueName])
      this.form.get('starting_date')!.setValue(this.modelDates[this.periodStartKeyName])

      if (!this.model[this.periodEndValueName]) {
        this.form.get('ending_date')!.setValue(null)
        this.modelDates.endingDate = null
      } else {
        this.modelDates[this.periodEndKeyName] = this.dateService.convertToLocalDate(this.model[this.periodEndValueName], true)
        this.form.get('ending_date')!.setValue(this.modelDates[this.periodEndKeyName])
      }
    } else {
      this.hasStartingDate = false
      this.modelDates = pick(this.form.value, this.periodStartKeyName, this.periodEndKeyName)
    }

    this.initBranches()
    this.loaded = true

    this.form
      .get('branch')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.form.get('transferable')?.setValue(value?.allow_transfer)

        if (!this.form.get('branch')!.pristine && this.form.enabled) {
          // Save current areas if in edit/details mode before updating tree data
          const currentAreas = this.state !== 'create' ? [...(this.form.get('areas')!.value || [])] : []

          this.updateTreeAndAreasData(value)

          // In edit/details mode, restore saved areas or use existing
          if (this.state !== 'create' && currentAreas.length > 0) {
            this.selectedAreas = currentAreas
          }

          this.form.get('areas')!.setValue(this.selectedAreas)

          // Update the open once bu branch keep open
          this.setDefaultDomainValues()
          this.setDefaultValueToLimitations(value)
          this.updateTreeRef?.expandAll()
          this.detailsTreeRef?.expandAll()
        }
      })
  }

  /**
   * Update tree and areas data
   * @param value
   */
  updateTreeAndAreasData(value: any) {
    this.selectedBranch = value
    this.selectedEntrance = this.entrances ? this.entrances[0] : null
    if (this.state === 'create') {
      this.selectedAreas = []
    }

    if (!value) {
      if (this.state != 'details') {
        this.updateTreeRef.data = []
      } else {
        this.detailsTreeRef.data = []
      }
      this.restrictionsRef.selectedRestrictions = []
    }

    if (this.state != 'details' && this.updateTreeRef) {
      if (this.state == 'create') {
        this.updateTreeRef.data = this.entrances ? [...this.entrances] : []
        this.updateTreeRef?.initNodes()
        this.updateTreeRef.currentNode = this.updateTreeRef.nodes[0]
      } else {
        this.areas = this.selectedEntrance?.areas
        this.updateTreeRef.currentNode = this.updateTreeRef.nodes[0]
        this.updateTreeRef.expandAll()
      }
    } else {
      this.detailsTreeRef?.initNodes()
    }
  }

  /**
   * Init branches
   */
  initBranches() {
    if (this.state == 'create') {
      if (this.setDefaultBranch || this.branches.length == 1) {
        this.selectedBranch = this.branches[0]
      }
      const data = {}

      this.modelDates = pick(data, this.periodStartKeyName, this.periodEndKeyName)
    } else {
      this.selectedBranch = this.branches.find((branch) => branch?.id?.toString() == this.model?.event_id?.toString())
      this.updateTreeAndAreasData(this.selectedBranch)

      if (this.state == 'edit') {
        // Save current areas before updating tree
        const currentAreas = [...(this.model.areas || [])]

        this.updateTreeAndAreasData(this.selectedBranch)

        // Make sure areas aren't lost
        if (currentAreas.length > 0 && this.selectedAreas.length === 0) {
          this.selectedAreas = currentAreas
        }

        this.updateEntrancesAndAreasInput()
      }

      this.form.get('branch')!.setValue(this.selectedBranch)

      this.modelDates[this.periodStartKeyName] = this.dateService.convertToLocalDate(this.model[this.periodStartValueName])
      this.form.get('starting_date')!.setValue(this.modelDates[this.periodStartKeyName])

      if (!this.model[this.periodEndValueName]) {
        this.form.get('ending_date')!.setValue(null)
        this.modelDates.endingDate = null
      } else {
        this.modelDates[this.periodEndKeyName] = this.dateService.convertToLocalDate(this.model[this.periodEndValueName], true)
        this.form.get('ending_date')!.setValue(this.modelDates[this.periodEndKeyName].setHours(0, 0, 0, 0))
      }
    }

    if (this.state == 'create' && (this.setDefaultBranch || this.branches.length == 1)) {
      this.form.get('branch')!.setValue(this.selectedBranch)
      this.updateTreeAndAreasData(this.selectedBranch)
      this.updateTreeRef?.expandAll()
      this.detailsTreeRef?.expandAll()
    }
  }

  /**
   * Cancel button clicked method, reset form data and emit onClose output
   */
  cancel(e?: any, subscription?: Subscription) {
    e?.preventDefault()
    this.visible = false

    if (subscription) {
      subscription?.unsubscribe()
    }

    if (e?.pointerType == 'mouse') {
      this.loaded = false
      this.model = {}
      this.form.reset(this.defaultFormState)
      this.visible = false
      this.onClose.emit(true)
    }

    this.selectedBranch = null
    this.areas = []
  }

  /**
   * Change state component to edit when click on "edit" button
   * @param e
   */
  changeToEditState(e: Event | null) {
    if (e) e.preventDefault()

    // Save the current areas before changing state
    const currentAreas = [...(this.model.areas || [])]
      .map((areaId) => {
        // If we have area objects with IDs, use them
        if (typeof areaId === 'object' && areaId.id) {
          return areaId
        }

        // Otherwise find them in the tree
        let foundArea = null
        if (this.selectedBranch?.entrances) {
          for (const entrance of this.selectedBranch.entrances) {
            if (entrance.areas) {
              const area = entrance.areas.find((a: any) => a.id.toString() === areaId.toString())
              if (area) {
                foundArea = area
                break
              }
            }
          }
        }
        return foundArea
      })
      .filter((area) => area) // Remove any nulls

    // Change the state
    this.state = 'edit'

    // Build the form
    this.formBuild()

    // Set up data
    this.fetchData()

    // Force the selected areas after all data is set up
    setTimeout(() => {
      this.selectedAreas = currentAreas
      this.form.get('areas')!.setValue(currentAreas)
      this.updateEntrancesAndAreasInput()
      if (this.form.contains('create_pass')) {
        this.form.get('create_pass')!.setValue(this.model.create_pass)
      }
    }, 100)
  }

  /**
   * Ob dates changed method
   * @param dates
   */
  onDatesChange(dates: any) {
    if (dates?.[this.periodStartKeyName]) {
      this.hasStartingDate = true
    }
    for (const key in dates) {
      this.form.get(key)?.setValue(dates[key])
    }
  }

  /**
   * Is field is required
   * @param key Form key
   * @returns True/false
   */
  isRequired(key: string): boolean {
    const control: AbstractControl | null = this.form?.get(key)
    if (!control) {
      return false
    }
    return !!(control.validator && !control.valid && !control.disabled)
  }

  /**
   * On select branch update, entrance and employees arrays
   * @param e Html event
   * @param selectedBranch selected branch
   */
  onSelectBranch(e: any, _selectedBranch: EventWithStatus) {
    e?.preventDefault()
    this.selectedBranch = _selectedBranch
  }

  /**
   * Expand all pr collapse all tree
   * @param e For prevent default
   */
  expandOrCollapseAll(e: Event) {
    e.preventDefault()
    if (this.state != 'details') {
      if (this.updateTreeRef.isExpanded) {
        this.updateTreeRef.collapseAll()
      } else {
        this.updateTreeRef.expandAll()
      }
    } else {
      if (this.detailsTreeRef.isExpanded) {
        this.detailsTreeRef.collapseAll()
      } else {
        this.detailsTreeRef.expandAll()
      }
    }
  }

  /**
   * Node selection changed update areas array
   * @param node Selection node
   */
  nodeSelectionChanges(node: any) {
    const currentEntrance = this.entrances?.find((entrance: any) => entrance.id.toString() == node.data.id.toString())
    this.selectedEntrance = currentEntrance
    this.areas = currentEntrance?.areas
  }

  /**
   * Node checkbox selection changed
   * @param select The select
   */
  nodeCheckBoxSelection(node: TreeNode, select: boolean) {
    this.updateTreeRef.currentNode = node
    this.selectedEntrance = node.data
    this.areas = node.data.obj.areas

    // Toggle areas based on the 'select' parameter
    const areasToToggle = this.areas
    let selectedAreas = this.form.get('areas')!.value || []

    if (select) {
      // Select all areas under this entrance
      areasToToggle.forEach((area: any) => {
        if (!selectedAreas.some((a: any) => a.id.toString() === area.id.toString())) {
          selectedAreas.push(area)
        }
      })
    } else {
      // Deselect all areas under this entrance
      selectedAreas = selectedAreas.filter((area: any) => !areasToToggle.some((a: any) => a.id.toString() === area.id.toString()))
    }

    // Update the form control and area selections
    this.areaSelectionChanges(selectedAreas)
  }

  /**
   * When select or unselect all entrances update selected areas object for parent
   * @param entrance
   * @param select
   */
  selectOrUnselectAllParentAreas(entrance: any, select: boolean, updateState?: boolean) {
    if (entrance) {
      if (!updateState) {
        entrance?.areas?.forEach((area: any) => {
          this.updateSelectedAreasList(area, select)
        })
      } else {
        const selectedAreasToAdd = entrance.areas.filter((area: any) => this.model.areas?.includes(Number.parseInt(area.id)))
        selectedAreasToAdd?.forEach((area: any) => {
          this.updateSelectedAreasList(area, select)
        })
      }
      if (entrance.parent_id) {
        const parentEntrance = this.entrances?.find((_entrance: any) => _entrance.id.toString() == entrance.parent_id[0].toString())
        this.selectOrUnselectAllParentAreas(parentEntrance, select, updateState)
      }
    }
  }

  /**
   * When select or unselect all entrances update selected areas object for children
   * @param entrance
   * @param select
   */
  selectOrUnselectAllChildrenAreas(entrance: any, select: boolean, updateState?: boolean) {
    const currentEntrance = this.entrances?.find((e: any) => e.id.toString() == entrance.id.toString())

    if (currentEntrance) {
      if (!updateState)
        currentEntrance.areas.forEach((area: any) => {
          this.updateSelectedAreasList(area, select)
        })
      else {
        const selectedAreasToAdd = entrance.areas.filter((area: any) => this.model.areas?.includes(Number.parseInt(area.id)))

        selectedAreasToAdd?.forEach((area: any) => {
          this.updateSelectedAreasList(area, select)
        })
      }
      currentEntrance.subEntrances.forEach((subEntrance: any) => {
        this.selectOrUnselectAllChildrenAreas(subEntrance, select, updateState)
      })
    }
  }

  updateSelectedAreasList(area: Area, select: boolean) {
    const index = this.selectedAreas.findIndex((_area: any) => _area.id.toString() == area.id.toString())
    if (select && !this.selectedAreas.find((v) => v?.id?.toString() == area?.id?.toString())) {
      this.selectedAreas.push(area)
    } else if (!select) {
      if (index > -1) this.selectedAreas.splice(index, 1)
    }
  }

  /**
   * Get branch by event id
   * @param event_id Event id
   * @returns Branch
   */
  branch(event_id: any) {
    return this.branches?.find((branch: any) => branch?.id.toString() == event_id?.toString())
  }

  branchImageCallback = (args: any): any => {
    return this.branchImage(args)
  }

  shortNameCallback = (args: any): any => {
    return this.commonService.getShortName(args)
  }

  /**
   * Get branch image
   * @param branch
   * @returns
   */
  branchImage(branch: any) {
    return branch?.image || this.client?.image
  }

  /**
   * Boolean get translate
   * @param value Value if no value return empty else return allow or deny
   * @returns
   */
  booleanTranslate(value: any) {
    return `COMMON.TAGS.${value ? 'ALLOW' : 'DENY'}`
  }

  /**
   * Update entrances and areas input when edit mode or from parent component
   */
  async updateEntrancesAndAreasInput() {
    // Tree not rendered yet (e.g. state just switched) — changeToEditState retries via setTimeout
    if (!this.updateTreeRef) return

    // Set period start date
    this.form.get(this.periodStartKeyName)!.setValue(this.dateService.convertToLocalDate(this.model[this.periodStartValueName]))

    // Set period end date if available
    if (this.model[this.periodEndValueName]) {
      this.form.get(this.periodEndKeyName)!.setValue(this.dateService.convertToLocalDate(this.model[this.periodEndValueName], true))
    }

    // If there's a helper method to get areas, make sure to call it here. Otherwise:
    const areasFromService = this.branchService.getAreasArrayByIdsArray(this.model, this.selectedBranch)

    if (!areasFromService) {
      this.form.get('areas')!.setValue(areasFromService)
    }

    // Normalize model.areas to string[] — can be an array, a comma-separated string, or undefined
    const rawAreas = this.model.areas
    const modelAreasAsStrings: string[] = Array.isArray(rawAreas)
      ? rawAreas.map((id: any) => id.toString())
      : typeof rawAreas === 'string'
      ? rawAreas.split(',').map((id) => id.trim()).filter(Boolean)
      : []

    // Collect selected areas from ALL entrances
    let selectedAreas: any[] = []
    this.selectedBranch?.entrances?.forEach((entrance: any) => {
      if (entrance.name !== 'event_unused_entrance' && entrance.areas?.length) {
        const matchedAreas = entrance.areas.filter((area: any) => modelAreasAsStrings.includes(area.id.toString()))
        if (matchedAreas?.length) {
          selectedAreas.push(...matchedAreas)
        }
      }
      entrance.subEntrances?.forEach((subEntrance: any) => {
        if (subEntrance.areas?.length) {
          const matchedSubAreas = subEntrance.areas.filter((area: any) => modelAreasAsStrings.includes(area.id.toString()))
          if (matchedSubAreas?.length) {
            selectedAreas.push(...matchedSubAreas)
          }
        }
      })
    })

    // De-duplicate if necessary
    selectedAreas = [...new Map(selectedAreas.map((item) => [item.id, item])).values()]

    // Prepare the area selections in the tree
    const entranceNodes: TreeNode[] = []
    this.updateTreeRef.selection = []

    for (const node of this.updateTreeRef?.serializedValues || []) {
      const entranceAreas = node.data.obj?.areas || []

      // Find areas that match this node
      const matchingAreas = entranceAreas.filter((area: any) => selectedAreas.some((selectedArea: any) => selectedArea?.id?.toString() === area?.id?.toString()))

      const allAreasSelected = matchingAreas.length === entranceAreas.length && entranceAreas.length > 0
      const someAreasSelected = matchingAreas.length > 0 && matchingAreas.length < entranceAreas.length

      node.selected = allAreasSelected
      node.partialSelected = someAreasSelected

      if (allAreasSelected || someAreasSelected) {
        entranceNodes.push(node)
      }
    }

    this.updateTreeRef.selection = entranceNodes
    ;(this.updateTreeRef.treeReference.selection as any).set(this.updateTreeRef.selection)

    // Update component-level selectedAreas and the form control
    this.selectedAreas = selectedAreas
    this.form.get('areas')!.setValue(this.selectedAreas)

    // If at least one node was found, set currentNode to the first node with selections
    if (entranceNodes.length > 0) {
      this.updateTreeRef.currentNode = entranceNodes[0]
      this.selectedEntrance = this.updateTreeRef.currentNode.data.obj
      this.areas = this.selectedEntrance?.areas
    } else if (this.updateTreeRef.nodes.length > 0) {
      // Fallback if no matched entrances
      this.updateTreeRef.currentNode = this.updateTreeRef.nodes[0]
      this.selectedEntrance = this.updateTreeRef.currentNode.data.obj
      this.areas = this.selectedEntrance?.areas
    }

    // Optionally sync the listbox or other UI element
    this.areaSelectionChanges(this.selectedAreas, true)
  }

  /**
   * Area selection changes, update entrances tree selection
   * @param selectedAreas
   */
  areaSelectionChanges(selectedAreas: any[], _isUpdateState?: boolean) {
    // Ensure this overrides the base method if needed
    if (!this.updateTreeRef?.treeReference) {
      // Tree not ready, just update form value and internal state
      const currentSelectedAreas = Array.isArray(selectedAreas) ? selectedAreas : []
      this.form.get('areas')!.setValue(currentSelectedAreas)
      this.selectedAreas = currentSelectedAreas // Keep internal state consistent
      return
    }

    const currentSelectedAreas = Array.isArray(selectedAreas) ? selectedAreas : []
    const selectedAreaIds = new Set(currentSelectedAreas.map((area) => area?.id?.toString()).filter((id) => id))

    const newTreeSelection: TreeNode[] = []

    // Update tree nodes' visual state
    for (const node of this.updateTreeRef.serializedValues || []) {
      const entranceAreas = node.data.obj?.areas || []
      if (!entranceAreas.length) {
        node.selected = false
        node.partialSelected = false
        continue
      }

      const matchingAreasCount = entranceAreas.filter((area: any) => selectedAreaIds.has(area.id.toString())).length

      const allAreasSelected = matchingAreasCount === entranceAreas.length
      const someAreasSelected = matchingAreasCount > 0 && matchingAreasCount < entranceAreas.length

      node.selected = allAreasSelected
      node.partialSelected = someAreasSelected

      // Build the new selection array for the tree component
      if (allAreasSelected || someAreasSelected) {
        newTreeSelection.push(node)
      }
    }

    // *** Update the tree component's selection state ***
    // Assigning a new array reference is often key to trigger UI update
    this.updateTreeRef.selection = newTreeSelection
    ;(this.updateTreeRef.treeReference.selection as any).set([...newTreeSelection])

    // Update component's internal state
    this.selectedAreas = currentSelectedAreas

    // *** Update form control value ONCE at the end ***
    this.form.get('areas')!.setValue(this.selectedAreas)

    // Optional: Trigger change detection if needed
    // this.cdRef.markForCheck();
  }

  onEntranceNodeClick(node: TreeNode) {
    // Toggle the selection of all areas under the entrance
    if (this.updateTreeRef.currentNode === node) {
      const areasToToggle = this.areas
      let selectedAreas = this.form.get('areas')!.value || []

      const isCurrentlySelected = node.checked

      if (isCurrentlySelected) {
        // Deselect all areas under this entrance
        selectedAreas = selectedAreas.filter((area: any) => !areasToToggle.some((a: any) => a.id.toString() === area.id.toString()))
      } else {
        // Select all areas under this entrance
        areasToToggle.forEach((area: any) => {
          if (!selectedAreas.some((a: any) => a.id.toString() === area.id.toString())) {
            selectedAreas.push(area)
          }
        })
      }

      // Update the form control and area selections
      this.areaSelectionChanges(selectedAreas)
    }
  }

  setDefaultDomainValues() {
    let openOnce = 0
    let transferable = false

    if (this.selectedBranch) {
      openOnce = this.selectedBranch?.keep_open ? 1 : 0
      transferable = this.selectedBranch?.allow_transfer ? true : false
    }

    this.form.get('open_once')!.setValue(openOnce)
    this.form.get('transferable')?.setValue(transferable)
  }

  /**
   * Set default value to limitations when the branch has limitations and state is create
   * @param value Selected model
   */
  setDefaultValueToLimitations(value: any) {
    if (this.model?.role == 'guest' || (this.model?.role == 'delivery' && this.model?.time_limitations?.length)) {
      this.restrictionsRef.selectedRestrictions = []

      const restriction: any = {}
      restriction.hourFirstRange = new Date(this.model.limitations_start)
      restriction.hourSecondRange = new Date(this.model.limitations_end)
      const day = restriction.hourFirstRange.getDay() + 1
      restriction.daysWeek = this.commonService.getDayList(0, true).filter((el: any) => el.value == day)
      this.restrictionsRef.addNewRestriction(null, restriction)
    } else if (value?.time_limitations?.length) {
      if (!this.form?.get('related_id')?.value) {
        this.model.time_limitations = value.time_limitations
        this.restrictionsRef.selectedRestrictions = []
        const restrictions = this.dateService.buildAndGetRestrictionsByTimeLimitation(value.time_limitations, 'days_hours_allowed')

        restrictions.forEach((element) => {
          this.restrictionsRef.addNewRestriction(null, element)
        })
      }
    }
  }

  booleanForTheDetails(value: boolean) {
    return `COMMON.ACTIONS.${value ? 'YES' : 'NO'}`
  }

  /**
   * When the current page is employee/authorized fetch the client role
   * @param category
   */
  fetchClientRolesByCategoryType(category: 'employees' | 'authorized' | 'vehicles' | 'hme') {
    this.clientRoleService
      ?.getClientRoles(category)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((roles: ClientRole[]) => {
        const sortedRoles = [...roles].sort((a, b) => a.name.localeCompare(b.name))
        // Store all roles for later filtering
        this.allClientRoles = sortedRoles

        // Initial filtering based on current event (if already set)
        this.filterClientRolesByEvent(this.selectedBranch?.id)

        // Set the initial value if in edit/details mode
        if (this.state !== 'create') {
          this.form.get('client_role')!.setValue((this.clientRolesListConfig.data as ClientRole[]).find((v) => v.id.toString() === this.model.client_role?.toString())?.id || '')
        }

        // Update validators after initial load
        this.updateClientRoleValidators(this.clientRolesListConfig.data as ClientRole[])

        // The roles (and with them their booking config) are only now known — settle the
        // room-booking checkbox, which is derived from the selected role. YB-2066.
        this.syncBookingEnabledWithRole()
      })

    // Follow the role wherever it is set from: the dropdown, an edit-mode restore, or a
    // switch between events in multi-event create. Programmatic sets are honoured but never
    // overrule a decision already recorded on the pass — see syncBookingEnabledWithRole.
    this.form
      .get('client_role')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncBookingEnabledWithRole())

    // `form.enable()` — which every save handler calls when the server refuses — re-enables EVERY
    // control, the booking box included, and would hand back a tick the role does not allow.
    // Re-assert it here instead of chasing the dozen enable() call sites and the next one someone
    // adds. `emitEvent: false` is what stops this from re-entering itself.
    this.form
      .get('booking_enabled')
      ?.statusChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const control = this.form.get('booking_enabled')
        if (control?.enabled && !this.bookingBoxAlwaysEnabled() && !this.roleAllowsBooking(this.form.get('client_role')?.value)) control.disable({ emitEvent: false })
      })

    // Add subscription to branch changes to filter roles
    this.form
      .get('branch')!
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((selectedBranch) => {
        this.filterClientRolesByEvent(selectedBranch?.id)
      })
  }

  /**
   * YB-2066 — may passes carrying this client role book rooms?
   *
   * The role is where room booking is configured (it is also the credit pool the bookings are
   * charged against), so it is what the per-pass checkbox is derived from.
   */
  protected roleAllowsBooking(roleId: unknown): boolean {
    if (roleId === null || roleId === undefined || roleId === '') return false
    const roles = (this.clientRolesListConfig?.data as ClientRole[] | undefined) || []
    return roles.find((role) => role.id?.toString() === String(roleId))?.booking?.enabled === true
  }

  /**
   * YB-2066 — may the room-booking box be used even when the role forbids booking?
   *
   * Employee passes: yes. The portal must be able to grant ONE employee room booking without
   * opening it to their whole role, which is the `true` half of the per-pass override —
   * `passes_extra.booking_enabled = 1` beats a role with booking off, server-side. PAPI has always
   * honoured it; the box being greyed is what made it unreachable.
   *
   * Authorized (and kindergarten/residential) passes: no — the role stays the gate there, so the
   * box greys out exactly as before. Keyed on the MODULE, not on the modal class: employee-modal
   * also renders authorized passes, and those must follow the authorized rule.
   */
  protected bookingBoxAlwaysEnabled(): boolean {
    return this.activeModuleName === 'employees'
  }

  /**
   * Re-derive the room-booking checkbox from the currently selected client role.
   *
   * Pick a role that may book rooms and the box comes up ticked; pick one that may not and it is
   * greyed out, because `client_roles.booking.enabled` is the permission and the pass flag is only
   * ever an exception to it. Untick it on a booking-enabled role and that exception is recorded.
   *
   * @param roleJustPicked true when the user chose the role in the dropdown. The new role's own
   *        setting then wins; without it a value already on the pass (a deliberate revocation, or a
   *        grant made outside the portal) is left alone.
   */
  protected syncBookingEnabledWithRole(roleJustPicked: boolean = false) {
    const control = this.form?.get('booking_enabled')
    if (!control) return

    // The roles arrive AFTER formBuild(), and until they do `roleAllowsBooking` answers false for
    // every role — including one that may in fact book. Deciding anything here used to write a
    // `false` that outlived the roles landing: `false` is not `== null`, so the inherit-from-role
    // tick below could never fire again, and the box stayed unticked-but-enabled for a role that
    // permits booking. Ticking it then resolved to "agrees with the role" = null, so the save
    // appeared to do nothing, and an untouched save wrote a revocation nobody asked for.
    // Nothing is knowable yet, so decide nothing: keep the box inert and leave the value alone.
    if (!(this.clientRolesListConfig?.data as ClientRole[] | undefined)?.length) {
      if (control.enabled && !this.bookingBoxAlwaysEnabled()) control.disable({ emitEvent: false })
      this.cdRef?.markForCheck()
      return
    }

    const allowed = this.roleAllowsBooking(this.form.get('client_role')?.value)

    if (allowed) {
      // `null`/`undefined` = nothing decided for this pass (every pass predating YB-2066, and every
      // new one), so it inherits the role's yes. An explicit `false` is a revocation and stands.
      if (roleJustPicked || control.value == null) control.setValue(true, { emitEvent: false })
    } else if (roleJustPicked || control.value !== true) {
      // Role says no. Store `null` (nothing decided), NEVER `false`: the field is tri-state and
      // `false` is the wire value for a per-pass REVOCATION that outranks the role. Synthesising one
      // here is indistinguishable from a decision the user made, and it sticks — see above. `null`
      // renders unticked exactly as `false` did, and resolves to null on save either way.
      // A `true` already on the pass is a grant made elsewhere (API, import) and is shown as-is
      // rather than silently dropped — but the box is greyed either way.
      control.setValue(null, { emitEvent: false })
    }

    // Disable the CONTROL rather than binding `[attr.disabled]` in the template. The box is rendered
    // inside an OnPush `@for` over checkboxListConfig, so a flag mutated on a config object only
    // reaches the DOM on the next change-detection pass — which is why the greying-out used to lag a
    // role behind, while the tick beside it landed at once. A control's disabled state is pushed
    // straight to the element by the ControlValueAccessor, the same synchronous path setValue takes.
    //
    // The cost is that `form.value` omits a disabled control. `.value` still reads back through
    // `form.get()`, which is how every save path here reads it — except employee-modal's
    // prepareForSave, which starts from `form.value` and therefore sets the field explicitly.
    // An employee pass keeps the box usable whatever the role says — see bookingBoxAlwaysEnabled.
    // The VALUE still comes from the role above, so the box merely starts unticked there.
    const keepEnabled = allowed || this.bookingBoxAlwaysEnabled()
    if (keepEnabled && control.disabled) control.enable({ emitEvent: false })
    else if (!keepEnabled && control.enabled) control.disable({ emitEvent: false })

    this.cdRef?.markForCheck()
  }

  /**
   * What to SEND for `booking_enabled` — an override, or nothing at all.
   *
   * PAPI reads the pass value when it is non-null and the role's only otherwise, so writing a `true`
   * that merely repeats the role would freeze the pass: turning booking off on the role afterwards
   * would no longer revoke it. Agreement with the role is therefore stored as `null` (inherit) and
   * only a disagreement is persisted.
   *
   * `null` rather than an omitted key on purpose — on update, omitting means "leave as is", which
   * would make re-ticking a previously revoked box do nothing.
   */
  protected resolveBookingEnabledOverride(roleId: unknown, checked: boolean | null | undefined): boolean | null {
    // EMPLOYEE passes: the box IS the decision, in both directions. Ticked stores a grant (`true`),
    // unticked a revocation (`false`), neither of them derived from the role — the portal has to be
    // able to add AND remove room booking per pass whatever the client role says. The consequence is
    // deliberate: once an employee pass has been saved it carries an explicit value, so the role's
    // booking setting no longer governs it. It seeds the box (see syncBookingEnabledWithRole) and
    // still governs every employee pass nobody has edited, which stay `null`.
    if (this.bookingBoxAlwaysEnabled()) return checked === true

    // AUTHORIZED (and kindergarten/residential): the role stays the gate, so only a DISAGREEMENT is
    // persisted. Writing a `true` that merely repeats the role would freeze the pass — turning
    // booking off on the role afterwards would no longer revoke it. Agreement is stored as `null`
    // (inherit), and `null` rather than an omitted key on purpose: on update, omitting means "leave
    // as is", which would make re-ticking a previously revoked box do nothing.
    if (this.roleAllowsBooking(roleId)) return checked === false ? false : null
    return checked === true ? true : null
  }

  private filterClientRolesByEvent(eventId?: number | string) {
    if (!this.allClientRoles || this.allClientRoles.length === 0) {
      return
    }

    // Filter roles: include those without event_id or with matching event_id
    const filteredRoles = this.allClientRoles.filter((role) => {
      return !role.event_id || role.event_id.toString() === eventId?.toString()
    })

    // Update the dropdown data
    this.clientRolesListConfig.data = filteredRoles

    // Update validators for authorized based on available roles
    this.updateClientRoleValidators(filteredRoles)

    // If current selection is no longer valid, clear it — but never when the client_role is locked
    // (residential pins the owner/tenant/guest resident type). Keyed off lock INTENT, not the
    // control's enabled state, which can be flipped by the dropdown's late CVA attachment.
    const currentRoleId = this.form.get('client_role')!.value
    if (currentRoleId && !this.isClientRoleLocked() && !filteredRoles.some((role) => role.id.toString() === currentRoleId.toString())) {
      this.form.get('client_role')!.setValue('')
    }
  }

  /**
   * Whether the client_role is pinned/locked by the caller (residential owner/tenant/guest). Base
   * default is "not locked"; PassModalComponent overrides it from its lockedClientRole input. Guards
   * that would clear/reset client_role check this so a pinned value is never wiped.
   */
  protected isClientRoleLocked(): boolean {
    return false
  }

  private updateClientRoleValidators(filteredRoles: ClientRole[]) {
    const clientRoleControl = this.form?.get('client_role')
    if (!clientRoleControl) {
      return
    }

    // For authorized module, make client_role required if there are available roles
    if (this.activeModuleName === 'authorized') {
      if (filteredRoles && filteredRoles.length > 0) {
        // Add required validator
        clientRoleControl.setValidators([Validators.required])
      } else {
        // Remove required validator
        clientRoleControl.clearValidators()
      }
      clientRoleControl.updateValueAndValidity()
    } else {
      // For all other modules (employees, timewatch, etc.), client_role is always required
      // regardless of available roles
      clientRoleControl.setValidators([Validators.required])
      clientRoleControl.updateValueAndValidity()
    }
  }

  fetchAssignableClientRolesByCategoryType() {
    this.clientRoleService
      ?.getClientRoles('authorized')
      .pipe(take(1))
      .subscribe((roles: ClientRole[]) => {
        const sortedRoles = [...roles].sort((a, b) => a.name.localeCompare(b.name))
        this.assignableClientRolesListConfig.data = sortedRoles
      })
  }

  formBuild() {/* intentionally empty */}

  save(_e: Event) {/* intentionally empty */}
}
